import React from 'react';
import { Sparkline } from './Sparkline';

/**
 * MetricCard — hero outcome / KPI card.
 *
 * Used for the Overview page's four hero outcomes (Hours saved /
 * Realized ROI / Throughput / Risk avoided) and elsewhere on
 * Governance / Trust dashboards.
 *
 * The card composes a leading icon chip, a label, the headline value
 * (with optional unit), an optional trend delta line, and an optional
 * sparkline. Hovers lift 1px per the global card pattern.
 *
 * @param {Object} props
 * @param {string}             props.label
 * @param {React.ReactNode}    props.value
 * @param {React.ReactNode}    [props.unit]
 * @param {React.ReactNode}    [props.icon]
 * @param {'action'|'brand'|'success'|'warning'|'danger'|'trust'|'cyan'|'rose'|'muted'} [props.tone='action']
 * @param {Object}             [props.delta]
 *   `{ value, direction: 'up'|'down', baseline?, intent?: 'positive'|'negative'|'neutral' }`
 *   The intent decouples direction from "is this good?" — e.g. risk-avoided going *up*
 *   is positive (intent='positive', direction='up'), but trust-score going *down* is
 *   negative even though the arrow points down.
 * @param {number[]}           [props.trend]
 *   30-point sparkline series.
 * @param {'line'|'bar'}       [props.trendVariant='line']
 * @param {() => void}         [props.onClick]
 *   When provided, the card becomes interactive.
 */
export function MetricCard({
  label,
  value,
  unit,
  icon,
  tone = 'action',
  delta,
  trend,
  trendVariant = 'line',
  onClick,
}) {
  const ICON_BG = {
    action:  'bg-v2-action-soft text-v2-action',
    brand:   'bg-v2-brand-soft  text-v2-brand-strong',
    success: 'bg-v2-success-soft text-v2-success',
    warning: 'bg-v2-warning-soft text-v2-warning',
    danger:  'bg-v2-danger-soft  text-v2-danger',
    trust:   'bg-v2-trust-soft   text-v2-trust',
    cyan:    'bg-v2-cyan-soft    text-v2-cyan',
    rose:    'bg-v2-rose-soft    text-v2-rose',
    muted:   'bg-v2-muted        text-v2-ink-muted',
  }[tone] || 'bg-v2-action-soft text-v2-action';

  const intent = delta?.intent
    || (delta?.direction === 'up' ? 'positive' : 'negative');

  const deltaToneClass =
    intent === 'positive' ? 'text-v2-success' :
    intent === 'negative' ? 'text-v2-danger' :
                            'text-v2-ink-muted';

  const arrow = delta?.direction === 'up' ? '▲' : delta?.direction === 'down' ? '▼' : '';

  const isInteractive = typeof onClick === 'function';

  return (
    <div
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isInteractive
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } }
          : undefined
      }
      className={
        'bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm ' +
        'p-4 transition-[transform,box-shadow,border-color] duration-v2 ease-v2 ' +
        (isInteractive ? 'v2-focus-ring cursor-pointer hover:-translate-y-px hover:shadow-v2-md hover:border-v2-border' : '')
      }
    >
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-2">
        {icon && (
          <span aria-hidden="true" className={'w-[18px] h-[18px] rounded flex items-center justify-center ' + ICON_BG}>
            {icon}
          </span>
        )}
        <span>{label}</span>
      </div>
      <div className="text-[28px] font-extrabold tracking-tight leading-none text-v2-ink tabular-nums">
        {value}
        {unit && (
          <span className="text-[13px] text-v2-ink-secondary font-medium ml-1">{unit}</span>
        )}
      </div>
      {delta && (
        <div className={'text-[11.5px] font-semibold mt-1 ' + deltaToneClass}>
          {arrow && <span aria-hidden="true">{arrow}</span>} {delta.value}
          {delta.baseline && <span className="text-v2-ink-muted font-normal"> · {delta.baseline}</span>}
        </div>
      )}
      {trend && trend.length >= 2 && (
        <div className="mt-2.5">
          <Sparkline data={trend} variant={trendVariant} tone={tone} height={28} />
        </div>
      )}
    </div>
  );
}

export default MetricCard;
